import { FormEvent, ReactNode } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';
import { capitalize } from 'lodash';

import { OrderType } from '#/api/orders';

import { c080820, c7239C4, cFFFFFF } from '#/colors';

import {
  Tabs,
  TabList as ReachTabList,
  Tab as ReachTab,
  TabPanels,
  TabPanel,
} from '#/components/common/tabs';
import toast from '#/components/common/toast';
import Title from '#/components/common/toast/Title';
import OrderSideIcon from '#/components/shared/OrderSideIcon';

import {
  OrderFormProvider,
  OrderFormView,
  useOrderFormActions,
  useOrderFormView,
} from '#/features/options/order-builder/form-context';
import {
  OrderSubmitProvider,
  useOrderSubmit,
} from '#/features/options/order-builder/submit-context';
import { logException } from '#/features/logging/logging';

import TradeSidePicker from './TradeSidePicker';
import InputAmount from './InputAmount';
import InputLimitPrice from './InputLimitPrice';
import SubmitOrder from './SubmitOrder';
import OrderSummary from './OrderSummary';

interface Props {
  readonly className?: string;
}

interface Tab {
  id: OrderType;
  label: string;
  children: ReactNode;
}

const TABS: Tab[] = [
  {
    id: 'MARKET',
    label: 'Market',
    children: (
      <>
        <TradeSidePicker />
        <InputAmount />
      </>
    ),
  },
  {
    id: 'LIMIT',
    label: 'Limit',
    children: (
      <>
        <TradeSidePicker />
        <InputLimitPrice />
        <InputAmount />
      </>
    ),
  },
];

export default function OrderBuilder(props: Props) {
  return (
    <OrderFormProvider>
      <OrderSubmitProvider>
        <OrderBuilderContent {...props} />
      </OrderSubmitProvider>
    </OrderFormProvider>
  );
}

function OrderBuilderContent({ className }: Props) {
  const formView = useOrderFormView();
  const formActions = useOrderFormActions();
  const { submit } = useOrderSubmit();
  const activeTab = TABS.findIndex((tab) => tab.id === formView.type);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formView.isValid) return;

    submit((resp) => {
      if (resp.ok) {
        formActions.reset();
        orderSubmittedToast(formView);
      } else {
        const message = 'Failed to submit order';
        logException(new Error(message, { cause: resp.error }));
        toast.error(
          <>
            <Title>{message}</Title>
            {`[${resp.status}] ${resp.message}`}
          </>,
        );
      }
    });
  };
  const handleTabChange = (idx: number) =>
    formActions.updateType(TABS[idx]!.id);

  return (
    <FormContainer className={className} onSubmit={handleSubmit}>
      <Tabs index={activeTab} onChange={handleTabChange}>
        <TabList>
          {TABS.map(({ id, label }) => (
            <Tab key={id}>{label}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {TABS.map(({ id, children }) => (
            <TabPanel key={id}>
              <TabPanelContent>{children}</TabPanelContent>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
      <OrderSummary />
      <SubmitOrder />
    </FormContainer>
  );
}

function orderSubmittedToast(formView: OrderFormView) {
  toast.info(
    <>
      <Title>
        {`${capitalize(formView.type)} ${capitalize(formView.side)}` +
          ` Order Submitted`}
      </Title>
      {formView.amount.formattedValue}{' '}
      {formView.type === 'LIMIT' && `at ${formView.limitPrice.formattedValue}`}
    </>,
    {
      icon: <OrderSideIcon side={formView.side} />,
    },
  );
}

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TabList = styled(ReachTabList)`
  display: flex;
  background-color: ${c080820};
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const Tab = styled(ReachTab)`
  border: none;

  font-size: 14px;
  color: ${rgba(cFFFFFF, 0.5)};
  background-color: transparent;
  flex: 1 1 0px;
  height: 40px;
  font-size: 14px;

  &[data-selected] {
    color: ${cFFFFFF};
    background-color: ${c7239C4};
  }
`;

const TabPanelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
